import rehypeParse from "rehype-parse";
import rehypeRemark from "rehype-remark";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import remarkParse from "remark-parse";
import remarkStringify from "remark-stringify";
import { unified } from "unified";
import { selectAll } from "hast-util-select";
import { h } from "hastscript";
import type { Root } from "hast";
import { toHtml } from "hast-util-to-html";
import type { Options } from "hast-util-to-mdast";
import { Html } from "mdast";

export function markdownFromHTML(html: string): string {
  // Pre-process the HTML to replace mark and colored span tags with placeholders
  // that won't be converted by rehypeRemark
  let processedHtml = html;

  // Replace span tags with color styles with placeholders
  processedHtml = processedHtml.replace(
    /<span style="color: (.*?)">(.*?)<\/span>/g,
    (_, color, content) =>
      `<!--COLOR_START:${color}-->${content}<!--COLOR_END-->`,
  );

  // Replace member mention span tags with placeholders
  processedHtml = processedHtml.replace(
    /<span data-id="@\[(.*?):(.*?)\]" data-mention="member">(.*?)<\/span>/g,
    (_, id, name, content) =>
      `<!--MEMBER_MENTION_START:${id}:${name}-->${content}<!--MEMBER_MENTION_END-->`,
  );

  // Replace tag mention span tags with placeholders
  processedHtml = processedHtml.replace(
    /<span data-id="#\[(.*?):(.*?)\]" data-mention="tag">(.*?)<\/span>/g,
    (_, id, displayValue, content) =>
      `<!--TAG_MENTION_START:${id}:${displayValue}-->${content}<!--TAG_MENTION_END-->`,
  );

  const marker = `marker${new Date().getTime()}`;

  const preserve = (state, node) => {
    const result: Html = { type: "html", value: toHtml(node) };
    state.patch(node, result);
    return result;
  };

  // Convert to markdown
  let markdown = unified()
    .use(rehypeParse, { fragment: true })
    .use(() => (tree: Root) => {
      const listItems = selectAll(
        'li[data-list-kind="ordered"][data-list-order]',
        tree,
      );
      listItems.forEach((listItem) => {
        listItem.children.unshift(
          h("span", `${marker}/${listItem.properties.dataListOrder}`),
        );
      });
      return tree;
    })
    .use(rehypeRemark, {
      handlers: {
        u: preserve,
        s: preserve,
        mark: preserve,
      },
    } as Options)
    .use(remarkGfm)
    .use(remarkStringify)
    .processSync(processedHtml)
    .toString();

  markdown = markdown.replace(
    /<!--COLOR_START:(.*?)-->(.*?)<!--COLOR_END-->/g,
    (_, color, content) => `<span style="color: ${color}">${content}</span>`,
  );

  // Replace member mention placeholders with @[id:name] format
  markdown = markdown.replace(
    /<!--MEMBER_MENTION_START:(.*?):(.*?)-->(.*?)<!--MEMBER_MENTION_END-->/g,
    (_, id, name) => `@[${id}:${name}]`,
  );

  // Replace tag mention placeholders with #[id:displayValue] format
  markdown = markdown.replace(
    /<!--TAG_MENTION_START:(.*?):(.*?)-->(.*?)<!--TAG_MENTION_END-->/g,
    (_, id, displayValue) => `#[${id}:${displayValue}]`,
  );

  // Replace the markers with the correct list numbers
  markdown = markdown.replace(
    new RegExp(`(\\d+)\\. ${marker}/(\\d+)\\n\\n *`, "g"),
    (_, _currentNumber, originalNumber) => `${originalNumber}. `,
  );

  return markdown;
}

export function htmlFromMarkdown(markdown: string): string {
  // Pre-process markdown to handle @[id:name] mentions
  let processedMarkdown = markdown;

  // Replace @[id:name] mentions with HTML spans
  processedMarkdown = processedMarkdown.replace(
    /@\[(.*?):(.*?)\]/g,
    (_, id, name) =>
      `<span data-id="@[${id}:${name}]" data-mention="member">@${name}</span>`,
  );

  // Replace #[id:displayValue] tag mentions with HTML spans
  processedMarkdown = processedMarkdown.replace(
    /#\[(.*?):(.*?)\]/g,
    (_, id, displayValue) =>
      `<span data-id="#[${id}:${displayValue}]" data-mention="tag">#${displayValue}</span>`,
  );

  processedMarkdown = processedMarkdown.replace(
    /<u>(.*?)<\/u>/g,
    "<temp-underline>$1</temp-underline>",
  );

  // Convert markdown to HTML
  let html = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkHtml, {
      // Preserve raw HTML in the output
      allowDangerousHtml: true,
      sanitize: false,
    })
    .processSync(processedMarkdown)
    .toString();

  // Replace <del> tags with <s> tags for strikethrough
  html = html.replace(/<del>(.*?)<\/del>/g, "<s>$1</s>");

  html = html.replace(/<temp-underline>(.*?)<\/temp-underline>/g, "<u>$1</u>");

  return html;
}
